/* SUBJECT_KITS — for each subject "family", declare a set of interactive tools.
 *
 * Each tool has:
 *   - id: unique
 *   - label: display name
 *   - icon: Lucide icon component name (resolved in SubjectFooter)
 *   - color: pill background
 *   - build(x, y): returns an array of ONE prop element (persistent scene element)
 *     with a subject-tool-specific `type` that WhiteboardCanvas renders as an
 *     interactive widget.
 *
 * Detection: subject name substring match — keys are lowercase substrings.
 * See SubjectFooter.detectKit.
 */
import { rid } from "./wbUtils";

const propEl = (type, x, y, extras = {}) => ({
  id: rid(),
  type: "prop",
  propType: type,
  x, y,
  width: extras.width || 240,
  height: extras.height || 240,
  color: extras.color || "#0EA5E9",
  label: extras.label || "",
  state: extras.state || {},   // per-prop mutable state (fill level, rotation, on/off)
});

export const SUBJECT_KITS = {
  physics: {
    label: "Physics",
    color: "#0EA5E9",
    tools: [
      { id: "phys-projectile", label: "Projectile SIM ★", icon: "Zap", color: "#7C3AED",
        build: (x, y) => [propEl("sim-projectile", x, y, { width: 520, height: 340, label: "Projectile Motion", color: "#7C3AED", state: { angle: 45, speed: 60, running: false, t: 0 } })] },
      { id: "phys-circuit", label: "Battery + Bulb", icon: "Lightbulb", color: "#F59E0B",
        build: (x, y) => [propEl("circuit-bulb", x, y, { width: 380, height: 220, label: "Circuit", color: "#F59E0B", state: { closed: true } })] },
      { id: "phys-magnet",  label: "Magnet field",   icon: "Magnet",    color: "#DC2626",
        build: (x, y) => [propEl("magnet", x, y, { width: 300, height: 220, label: "Magnet", color: "#DC2626" })] },
      { id: "phys-lens",    label: "Convex lens",    icon: "Sparkles",  color: "#0EA5E9",
        build: (x, y) => [propEl("lens", x, y, { width: 360, height: 200, label: "Lens", color: "#0EA5E9" })] },
      { id: "phys-pendulum",label: "Pendulum",       icon: "Cog",       color: "#7C3AED",
        build: (x, y) => [propEl("pendulum", x, y, { width: 240, height: 320, label: "Pendulum", color: "#7C3AED", state: { swinging: true } })] },
      { id: "phys-wave",    label: "Wave",           icon: "Waves",     color: "#10B981",
        build: (x, y) => [propEl("wave", x, y, { width: 400, height: 160, label: "Wave", color: "#10B981", state: { freq: 2 } })] },
      { id: "phys-wave-interference", label: "Wave interference ★", icon: "Waves", color: "#0EA5E9",
        build: (x, y) => [propEl("sim-wave-interference", x, y, { width: 560, height: 320, label: "Two-source interference", color: "#0EA5E9", state: { f1: 2, f2: 2, phase: 0, t: 0, running: true } })] },
      { id: "phys-force",   label: "Force diagram",  icon: "Zap",       color: "#EF4444",
        build: (x, y) => [propEl("force-diagram", x, y, { width: 320, height: 220, label: "Forces", color: "#EF4444" })] },
    ],
  },
  chemistry: {
    label: "Chemistry",
    color: "#10B981",
    tools: [
      { id: "chem-ph-sim",   label: "pH scale SIM ★", icon: "TestTube", color: "#DC2626",
        build: (x, y) => [propEl("sim-ph", x, y, { width: 520, height: 220, label: "pH Scale", color: "#DC2626", state: { ph: 7 } })] },
      { id: "chem-beaker",   label: "Beaker",        icon: "Beaker",       color: "#0EA5E9",
        build: (x, y) => [propEl("beaker", x, y, { width: 200, height: 260, label: "Beaker", color: "#0EA5E9", state: { fill: 0.5, tint: "#60A5FA" } })] },
      { id: "chem-testtube", label: "Test tube",     icon: "TestTube",     color: "#7C3AED",
        build: (x, y) => [propEl("test-tube", x, y, { width: 90, height: 260, label: "Test tube", color: "#7C3AED", state: { fill: 0.4, tint: "#A78BFA" } })] },
      { id: "chem-pipette",  label: "Pipette",       icon: "Pipette",      color: "#DC2626",
        build: (x, y) => [propEl("pipette", x, y, { width: 80, height: 200, label: "Pipette", color: "#DC2626" })] },
      { id: "chem-burner",   label: "Bunsen burner", icon: "FlaskConical", color: "#F59E0B",
        build: (x, y) => [propEl("burner", x, y, { width: 160, height: 260, label: "Burner", color: "#F59E0B", state: { on: false } })] },
      { id: "chem-molecule", label: "H₂O molecule",  icon: "Atom",         color: "#0EA5E9",
        build: (x, y) => [propEl("molecule-h2o", x, y, { width: 260, height: 220, label: "H₂O", color: "#0EA5E9" })] },
      { id: "chem-ptable",   label: "Periodic snippet", icon: "Atom",      color: "#10B981",
        build: (x, y) => [propEl("periodic-snippet", x, y, { width: 380, height: 200, label: "Periodic", color: "#10B981" })] },
      { id: "chem-states",   label: "States of matter ★", icon: "FlaskConical", color: "#7C3AED",
        build: (x, y) => [propEl("sim-states-matter", x, y, { width: 520, height: 320, label: "Solid · Liquid · Gas", color: "#7C3AED", state: { phase: "solid", temp: 20 } })] },
    ],
  },
  math: {
    label: "Math",
    color: "#7C3AED",
    tools: [
      { id: "math-fn-grapher", label: "Grapher SIM ★", icon: "Sigma", color: "#7C3AED",
        build: (x, y) => [propEl("sim-grapher", x, y, { width: 520, height: 380, label: "Function grapher", color: "#7C3AED", state: { fn: "x^2/50", xMin: -10, xMax: 10 } })] },
      { id: "math-scale",   label: "Scale",       icon: "Ruler",   color: "#F59E0B",
        build: (x, y) => [propEl("scale", x, y, { width: 500, height: 60, label: "Scale", color: "#F59E0B" })] },
      { id: "math-graph",   label: "Graph paper", icon: "Sigma",   color: "#7C3AED",
        build: (x, y) => [propEl("graph-paper", x, y, { width: 360, height: 360, label: "Graph", color: "#7C3AED" })] },
      { id: "math-abacus",  label: "Abacus",      icon: "Sigma",   color: "#0EA5E9",
        build: (x, y) => [propEl("abacus", x, y, { width: 340, height: 220, label: "Abacus", color: "#0EA5E9" })] },
      { id: "math-dice",    label: "Dice",        icon: "Sigma",   color: "#DC2626",
        build: (x, y) => [propEl("dice", x, y, { width: 100, height: 100, label: "Roll me", color: "#DC2626", state: { face: 6 } })] },
      { id: "math-fraction",label: "Fraction bar",icon: "Sigma",   color: "#10B981",
        build: (x, y) => [propEl("fraction-bar", x, y, { width: 400, height: 90, label: "Fraction", color: "#10B981", state: { parts: 4, filled: 1 } })] },
    ],
  },
  biology: {
    label: "Biology",
    color: "#22C55E",
    tools: [
      { id: "bio-mitosis", label: "Cell division SIM ★", icon: "Dna", color: "#22C55E",
        build: (x, y) => [propEl("sim-mitosis", x, y, { width: 560, height: 320, label: "Mitosis animation", color: "#22C55E", state: { phase: 0, running: true } })] },
      { id: "bio-punnett", label: "Punnett square ★", icon: "Dna", color: "#7C3AED",
        build: (x, y) => [propEl("sim-punnett", x, y, { width: 440, height: 380, label: "Punnett square", color: "#7C3AED", state: { p1: "Aa", p2: "Aa" } })] },
      { id: "bio-cell",  label: "Animal cell",  icon: "Atom",       color: "#22C55E",
        build: (x, y) => [propEl("cell", x, y, { width: 320, height: 260, label: "Cell", color: "#22C55E" })] },
      { id: "bio-dna",   label: "DNA helix",    icon: "Dna",        color: "#7C3AED",
        build: (x, y) => [propEl("dna", x, y, { width: 200, height: 340, label: "DNA", color: "#7C3AED" })] },
      { id: "bio-plant", label: "Plant/leaf",   icon: "Leaf",       color: "#10B981",
        build: (x, y) => [propEl("plant", x, y, { width: 240, height: 300, label: "Plant", color: "#10B981" })] },
      { id: "bio-heart", label: "Heart",        icon: "HeartPulse", color: "#EF4444",
        build: (x, y) => [propEl("heart", x, y, { width: 240, height: 240, label: "Heart", color: "#EF4444", state: { beating: true } })] },
    ],
  },
  english: {
    label: "English",
    color: "#EF4444",
    tools: [
      { id: "eng-speech", label: "Speech bubble", icon: "MessageSquare", color: "#EF4444",
        build: (x, y) => [propEl("speech", x, y, { width: 260, height: 140, label: "Say…", color: "#EF4444" })] },
      { id: "eng-quote",  label: "Quotation",     icon: "Quote",         color: "#0A0C10",
        build: (x, y) => [propEl("quote", x, y, { width: 400, height: 100, label: "Quote", color: "#0A0C10" })] },
    ],
  },
  geography: {
    label: "Geography",
    color: "#0EA5E9",
    tools: [
      { id: "geo-globe",   label: "Globe",         icon: "Globe2", color: "#0EA5E9",
        build: (x, y) => [propEl("globe", x, y, { width: 260, height: 260, label: "Globe", color: "#0EA5E9" })] },
      { id: "geo-compass", label: "Compass rose",  icon: "Compass",color: "#F59E0B",
        build: (x, y) => [propEl("compass-rose", x, y, { width: 240, height: 240, label: "NSEW", color: "#F59E0B" })] },
    ],
  },
  urdu: {
    label: "Urdu",
    color: "#059669",
    tools: [
      { id: "urdu-tracing", label: "Tracing lines", icon: "Ruler", color: "#059669",
        build: (x, y) => [propEl("urdu-tracing", x, y, { width: 560, height: 240, label: "Urdu tracing", color: "#059669", state: { text: "بسم اللہ" } })] },
      { id: "urdu-couplet", label: "Couplet", icon: "Quote", color: "#7C3AED",
        build: (x, y) => [propEl("urdu-couplet", x, y, { width: 520, height: 180, label: "Couplet", color: "#7C3AED", state: { line1: "خودی کو کر بلند اتنا کہ ہر تقدیر سے پہلے", line2: "خدا بندے سے خود پوچھے بتا تیری رضا کیا ہے" } })] },
      { id: "urdu-alphabet", label: "Alphabet", icon: "Atom", color: "#DC2626",
        build: (x, y) => [propEl("urdu-alphabet", x, y, { width: 620, height: 180, label: "حروف تہجی", color: "#DC2626" })] },
      { id: "urdu-rtl-text", label: "RTL text", icon: "MessageSquare", color: "#0EA5E9",
        build: (x, y) => [propEl("urdu-rtl-text", x, y, { width: 500, height: 120, label: "اردو متن", color: "#0EA5E9", state: { text: "یہاں اردو میں لکھیں" } })] },
    ],
  },
  general: {
    label: "General",
    color: "#6366F1",
    tools: [
      { id: "gen-ruler",   label: "Ruler",   icon: "Ruler",    color: "#F59E0B",
        build: (x, y) => [propEl("scale", x, y, { width: 500, height: 60, label: "Ruler", color: "#F59E0B" })] },
      { id: "gen-compass", label: "Compass", icon: "Compass",  color: "#DC2626",
        build: (x, y) => [propEl("compass-rose", x, y, { width: 240, height: 240, label: "Compass", color: "#DC2626" })] },
    ],
  },
};
